<script setup>
import { ref, computed, nextTick, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import markdownit from 'markdown-it';

const { t } = useI18n();

const md = markdownit({ linkify: true });

md.renderer.rules.link_open = (tokens, idx, options, env, self) => {
    const token = tokens[idx];
    token.attrPush(['target', '_blank']);
    token.attrPush(['class', 'link']);
    return self.renderToken(tokens, idx, options);
};

// Props
const props = defineProps({
    confirm: {
        type: Object,
        default: () => ({})
    }
});

// Emits
const emit = defineEmits(['close', 'ok', 'cancel']);

// Refs
const confirmButton = ref(null);

// Computed
const text = computed(() => {
    return md.render(props.confirm.text);
});

// Methods
const handleKeydown = (e) => {
    if (e.key === 'Escape') {
        close();
    }
};

const ok = () => {
    emit('ok');
    close();
};

const cancel = () => {
    emit('cancel');
    close();
};

const close = () => {
    emit('close');
};

// Lifecycle
onMounted(() => {
    document.addEventListener('keydown', handleKeydown);

    nextTick(() => {
        confirmButton.value.focus();
    });
});

onBeforeUnmount(() => {
    document.removeEventListener('keydown', handleKeydown);
});
</script>

<template>
    <!-- Fond -->
    <div
        class="fixed top-0 left-0 w-full h-full bg-black/50 flex-center"
        :style="{ zIndex: confirm.zIndex || 50 }"
    >
        <!-- Dialog -->
        <Transition appear enter-from-class="scale-95 opacity-0" enter-to-class="scale-100 opacity-100">
        <div
            class="dialog bg-app-surface1 rounded-xl overflow-hidden md:min-w-[360px] md:w-[40%] w-[90%] max-w-[600px] shadow-2xl max-h-[90vh] overflow-y-auto transition-all duration-100 ease-out flex flex-col"
            role="dialog"
            aria-modal="true"
            :aria-label="confirm.title || t('common.confirm')"
        >
            <!-- Header avec titre et X -->
            <div class="flex items-center justify-between px-6 py-4 border-b border-app-border">
                <h2 v-if="confirm.title" class="text-lg font-semibold text-app-text">{{ confirm.title }}</h2>
                <div v-else />
                <button
                    type="button"
                    class="inline-flex items-center justify-center w-8 h-8 rounded-full text-app-text-soft hover:bg-app-border transition-colors"
                    @click="close"
                    :aria-label="t('common.close')"
                    :title="t('common.close')"
                >
                    <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                </button>
            </div>

            <!-- Contenu -->
            <div class="flex-1 px-6 py-5">
                <p class="text-left break-words leading-relaxed text-app-text2" v-html="text"></p>
            </div>

            <!-- Footer -->
            <div class="flex gap-2 pt-3 px-6 py-6 justify-end">
                <button type="button" class="btn btn-cancel !rounded-full" @click="cancel">{{ t('common.cancel') }}</button>
                <button ref="confirmButton" type="button" class="btn btn-primary !rounded-full" @click="ok">{{ t('common.confirm') }}</button>
            </div>
        </div>
        </Transition>
    </div>
</template>